import { UseQueryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '../../utils/useApi'
import {
    DETECTION_EXPLORER_MAX_PAGE_SIZE,
    DETECTION_FIELD_UNSET,
    CategoryEnum,
    VehicleTypeEnum,
    DirectionEnum,
    CountryEnum
} from './config'

// The category/country/direction/vehicle_type list filters also accept the "not_set" sentinel to
// match detections where that field is unset (alongside, or instead of, real values).
const orUnset = <T extends z.ZodTypeAny>(schema: T) => z.union([schema, z.literal(DETECTION_FIELD_UNSET)])

// Same four fields on the response are now always a non-empty string (the backend sends "not_set"
// instead of null/""). Normalize defensively so the rest of the app never has to null-check them.
const unsettableField = z
    .string()
    .nullish()
    .transform((value) => (value == null || value === '' ? DETECTION_FIELD_UNSET : value))

// ============================================
// POST /platform/get_detections
// ============================================
// Mirrors the Python pydantic `GetDetectionsRequest`.
// Note: Pydantic's `from_` field is aliased to `from`; in JS `from` is not reserved,
// so we just use `from` directly. `JSON.stringify(date)` produces an ISO 8601 string,
// which the backend's `datetime.datetime` field parses natively.

const conf01 = z.number().min(0).max(1)

const GetDetectionsRequestSchema = z.object({
    lot: z.string(),
    from: z.date(),
    to: z.date(),
    device_uuids: z.array(z.string()).min(1).max(100),
    // Narrows to specific scanners by their numeric device_id. A device_uuid can be linked to
    // several stations and so report under multiple device_ids; this filters on those ids
    // (ANY match), AND-ed with device_uuids. Empty is rejected, so omit the key to not filter.
    device_ids: z.array(z.number().int()).min(1).optional(),
    page: z.number().int().min(1).default(1),
    category: z.array(orUnset(CategoryEnum)).min(1).optional(),
    vehicle_type: z.array(orUnset(VehicleTypeEnum)).min(1).optional(),
    // No orUnset for direction: INV is the catch-all, so there's no "not_set" direction to filter.
    direction: z.array(DirectionEnum).min(1).optional(),
    country: z.array(orUnset(CountryEnum)).min(1).optional(),
    lpr_conf_min: conf01.optional(),
    lpr_conf_max: conf01.optional(),
    lpness_min: conf01.optional(),
    lpness_max: conf01.optional(),
    license_plate: z.array(z.string()).min(1).optional(),
    // Narrows the result set to specific detections (used when applying an image request's
    // `detection_uuids` selector). The backend caps the count (MAX_DETECTION_UUIDS) and still
    // enforces the time window + device_uuids, so the caller keeps both alongside this.
    detection_uuids: z.array(z.string()).min(1).optional(),
    group_by_license_plate: z.boolean().optional(),
    page_size: z.number().int().min(1).max(DETECTION_EXPLORER_MAX_PAGE_SIZE).optional()
})

// Per-detection image artifact. `url` is the presigned URL (null until the image is
// uploaded or on a resolution failure); `requested` is true when a recent image request
// targeted this detection for this image type. All three keys (render/lp/roi) are always
// present — on a resolution failure `url` is null and `requested` is false.
const DetectionImageSchema = z.object({
    url: z.string().nullable(),
    requested: z.boolean()
})
export type DetectionImage = z.infer<typeof DetectionImageSchema>

const DetectionResultSchema = z.object({
    detection_uuid: z.string(),
    device_uuid: z.string(),
    device_id: z.number().int(),
    timestamp: z.coerce.date(),
    license_plate: z.string().nullable(),
    country: unsettableField,
    direction: unsettableField,
    category: unsettableField,
    vehicle_type: unsettableField,
    lpr_conf: z.number().nullable(),
    lpness: z.number().nullable(),
    images: z.object({
        render: DetectionImageSchema,
        lp: DetectionImageSchema,
        roi: DetectionImageSchema
    })
})

const GetDetectionsResponseSchema = z.object({
    results: z.array(DetectionResultSchema),
    total: z.number().int().nonnegative()
})

export type GetDetectionsRequest = z.infer<typeof GetDetectionsRequestSchema>
export type DetectionResult = z.infer<typeof DetectionResultSchema>
export type GetDetectionsResponse = z.infer<typeof GetDetectionsResponseSchema>

export { DetectionImageSchema, DetectionResultSchema, GetDetectionsRequestSchema, GetDetectionsResponseSchema }

/**
 * Page-based detections fetch. Changing any field in `request` (including `page`)
 * triggers a new fetch via the query key. Use `data.total` to compute total pages:
 * `Math.ceil(total / DETECTION_EXPLORER_PAGE_SIZE)`.
 */
export const useGetDetectionsQuery = (request: GetDetectionsRequest, options?: { enabled?: boolean }) => {
    const queryOptions: Omit<UseQueryOptions<GetDetectionsResponse, Error>, 'queryFn'> = {
        queryKey: ['detectionExplorerGetDetections', request],
        enabled: options?.enabled,
        retry: false,
        keepPreviousData: true
    }
    return useApi('/platform/get_detections', request, GetDetectionsResponseSchema, queryOptions)
}
